import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { CandidateDetailScreen } from '@/features/recruitment-board/candidate-detail-screen'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for one candidate's record, at `/recruitment/candidates/$candidateId`.
 *
 * Invariant 1 check (plan 57 §0.3): this folder holds `index.tsx` and
 * `openings.$openingId.tsx` as FLAT siblings — there is no `recruitment.tsx`
 * layout parent — so this file is another sibling, not a child of the board.
 * The board renders its own screen with no `<Outlet />`, which is exactly why
 * it must never become a layout parent.
 *
 * Guarded on `recruitment_candidate.view` — the detail endpoint authorises on
 * that key server-side; this only keeps an ungranted user out of a screen
 * they cannot use. The write actions inside follow their own grants.
 */
export const Route = createFileRoute('/_authenticated/recruitment/candidates/$candidateId')({
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'recruitment_candidate.view')) {
      throw redirect({ to: '/recruitment' })
    }
  },
  component: CandidateDetailRoute,
})

function CandidateDetailRoute() {
  const { candidateId } = Route.useParams()
  const navigate = useNavigate()

  return (
    <CandidateDetailScreen
      candidateId={candidateId}
      onBack={() => void navigate({ to: '/recruitment' })}
      // The convert endpoint answers with the refreshed candidate carrying
      // `employee_id` — that id IS the created employee's record address.
      onNavigateToEmployee={(employeeId) => {
        void navigate({ to: '/hr/employees/$employeeId', params: { employeeId } })
      }}
    />
  )
}
